import { useCallback, useMemo, useState } from 'react';

export interface AvatarEditorSnapshot {
  baseItemId: string | null;
  accessoryItemIds: readonly string[];
}

export interface UseAvatarEditorStateReturn {
  baseItemId: string | null;
  accessoryItemIds: readonly string[];
  isDirty: boolean;
  setBase: (itemId: string) => void;
  clearBase: () => void;
  addAccessory: (itemId: string) => void;
  removeAccessory: (itemId: string) => void;
  /** Replace the editor state and treat it as the new "saved" baseline. */
  resetToSaved: (snapshot: AvatarEditorSnapshot) => void;
  /** Discard pending edits and return to the last saved baseline. */
  cancel: () => void;
}

/**
 * Local UI state for the LitSquad avatar editor.
 *
 * Tracks the base character + ordered list of accessory overlays the user
 * is composing, plus a dirty flag computed against the last "saved"
 * snapshot. The snapshot is updated when the parent confirms a successful
 * upload.
 *
 * Stateless on inventory data — callers pass IDs in and look up image
 * URIs themselves from `useLitSquadInventory`.
 */
export function useAvatarEditorState(
  initial: AvatarEditorSnapshot = { baseItemId: null, accessoryItemIds: [] },
): UseAvatarEditorStateReturn {
  const [baseItemId, setBaseItemId] = useState<string | null>(initial.baseItemId);
  const [accessoryItemIds, setAccessoryItemIds] = useState<readonly string[]>(
    initial.accessoryItemIds,
  );
  const [savedBaseId, setSavedBaseId] = useState<string | null>(initial.baseItemId);
  const [savedAccessoryIds, setSavedAccessoryIds] = useState<readonly string[]>(
    initial.accessoryItemIds,
  );

  const isDirty = useMemo(() => {
    if (baseItemId !== savedBaseId) return true;
    if (accessoryItemIds.length !== savedAccessoryIds.length) return true;
    for (let i = 0; i < accessoryItemIds.length; i++) {
      if (accessoryItemIds[i] !== savedAccessoryIds[i]) return true;
    }
    return false;
  }, [baseItemId, accessoryItemIds, savedBaseId, savedAccessoryIds]);

  const setBase = useCallback((itemId: string) => {
    setBaseItemId(itemId);
  }, []);

  const clearBase = useCallback(() => {
    setBaseItemId(null);
    setAccessoryItemIds([]);
  }, []);

  const addAccessory = useCallback((itemId: string) => {
    setAccessoryItemIds((prev) => (prev.includes(itemId) ? prev : [...prev, itemId]));
  }, []);

  const removeAccessory = useCallback((itemId: string) => {
    setAccessoryItemIds((prev) => prev.filter((id) => id !== itemId));
  }, []);

  const resetToSaved = useCallback((snapshot: AvatarEditorSnapshot) => {
    setBaseItemId(snapshot.baseItemId);
    setAccessoryItemIds(snapshot.accessoryItemIds);
    setSavedBaseId(snapshot.baseItemId);
    setSavedAccessoryIds(snapshot.accessoryItemIds);
  }, []);

  const cancel = useCallback(() => {
    setBaseItemId(savedBaseId);
    setAccessoryItemIds(savedAccessoryIds);
  }, [savedBaseId, savedAccessoryIds]);

  return {
    baseItemId,
    accessoryItemIds,
    isDirty,
    setBase,
    clearBase,
    addAccessory,
    removeAccessory,
    resetToSaved,
    cancel,
  };
}
